import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LogOut, LayoutDashboard, Users, Receipt, Wallet, Clock, FileText,
  ClipboardList, CalendarCheck, Database, Menu, Sparkles
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import ThemeToggle from "./ThemeToggle";

const BRAND = "Sanju Animations IT Solutions";

const adminLinks = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/employees", label: "Employees", icon: Users },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/admin/reimbursements", label: "Reimbursements", icon: Receipt },
  { to: "/admin/salary", label: "Salary", icon: Wallet },
  { to: "/admin/payments", label: "Payments", icon: FileText },
  { to: "/admin/db-config", label: "Database", icon: Database },
];

const employeeLinks = [
  { to: "/employee", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/employee/attendance", label: "Attendance", icon: Clock },
  { to: "/employee/bills", label: "Bills", icon: Receipt },
  { to: "/employee/salary", label: "Salary", icon: ClipboardList },
  { to: "/employee/payslips", label: "Payslips", icon: FileText },
];

const SidebarContent = ({ links, user, onNavigate, onLogout }) => (
  <div className="flex flex-col h-full bg-white dark:bg-zinc-950">
    <div className="px-6 py-7 border-b border-zinc-100 dark:border-zinc-800">
      <Link to="/" onClick={onNavigate} data-testid="sidebar-brand" className="block">
        <div className="flex items-baseline gap-1">
          <span className="rule-text text-3xl text-zinc-900 dark:text-white">EMS</span>
          <span className="w-2 h-2 rounded-full bg-[#FB923C] inline-block mb-0.5"></span>
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400 mt-2">{BRAND}</div>
      </Link>
    </div>
    <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto scrollbar-thin">
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onNavigate}
            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                  : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={16} className={isActive ? "text-indigo-600 dark:text-indigo-300" : "text-zinc-400 dark:text-zinc-500 dark:text-zinc-400 dark:text-zinc-500"} />
                {l.label}
                {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-600 dark:bg-indigo-300"></span>}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
    <div className="p-3 border-t border-zinc-100 dark:border-zinc-800">
      <div className="px-3 pb-3 flex items-center gap-3">
        <div className="avatar-circle">
          {(user?.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate capitalize">{user?.role}</div>
        </div>
      </div>
      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-colors"
      >
        <LogOut size={16} /> Sign out
      </button>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const links = user?.role === "admin" ? adminLinks : employeeLinks;

  const handleLogout = () => {
    setMobileOpen(false);
    logout();
    navigate("/login");
  };
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <aside className="hidden lg:flex w-64 border-r border-zinc-100 dark:border-zinc-800 flex-shrink-0" data-testid="sidebar-desktop">
        <SidebarContent links={links} user={user} onNavigate={() => {}} onLogout={handleLogout} />
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm border-b border-zinc-100 dark:border-zinc-800 sticky top-0 z-40" data-testid="main-header">
          <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden rounded-lg" data-testid="mobile-menu-btn">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 border-r border-zinc-100 dark:border-zinc-800 dark:bg-zinc-950">
                <SidebarContent links={links} user={user} onNavigate={closeMobile} onLogout={handleLogout} />
              </SheetContent>
            </Sheet>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-orange-400 shrink-0" />
                <div className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 truncate">{BRAND}</div>
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-500 dark:text-zinc-400 mt-0.5 hidden sm:block">
                {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Live
              </span>
            </div>
          </div>
        </header>

        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] w-full mx-auto">
            <div key={location.pathname} className="page-enter">
              <Outlet />
            </div>
          </div>
          <footer className="border-t border-zinc-100 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 backdrop-blur-sm" data-testid="main-footer">
            <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-[1400px] mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs">
                <div>
                  <div className="font-semibold text-zinc-900 dark:text-zinc-100">{BRAND}</div>
                  <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">© {new Date().getFullYear()} · Employee Management System · All rights reserved</div>
                </div>
                <div className="text-zinc-400 dark:text-zinc-500 dark:text-zinc-400 font-medium">v2.1 · Built with care</div>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}
